// Formatering av målinger til den teksten operatøren leser.
//
// HVORFOR EN EGEN FIL MED TESTER: dette ser ut som pynt, men det er selve
// påstanden. «214°» og «214,3°» sier to helt ulike ting om hvor godt en peiler
// peiler, og «2,4 GHz» og «2437,000 MHz» skiller mellom «noe på wifi-båndet» og
// «denne kanalen». Antall desimaler ER informasjon, og da må reglene stå ett
// sted og være testet — ikke gjentas i seks skjermer med litt ulik avrunding.
//
// GJENNOMGÅENDE REGEL: enhet ALLTID med, og norsk desimalkomma. En operatør som
// leser «1180» må ikke måtte gjette om det er meter eller yards.
//
// ══ HVA SOM ER GENERISK, OG HVA SOM IKKE ER ════════════════════════════════
// Her ligger formaterere for STØRRELSER, ikke for domener: grader, meter, hertz
// og et sikkerhetstall mellom 0 og 1 betyr det samme på en kystpost og i en
// grøft ved en skogsvei. Frekvens er med nettopp fordi hertz ikke er en maritim
// enhet — en akustisk detektor og en KrakenRF trenger samme skala-logikk.
//
// Det som er BLITT IGJEN i NJORD er enheter og tekster som bærer domenet:
//   * `formaterFart` i KNOP — en jegertropp måler ikke fart i knop, og en
//     «generell» fartsformaterer med enhetsvalg ville blitt en abstraksjon
//     ingen av appene ba om.
//   * `maalingTekst`/`maalingslagNavn` — de leser KOP-broens `Maaling`-union
//     (peiling-avstand, lydsignatur, bilde …). Selve unionen er wire-formatet
//     til kystposten; ULL får sine egne målingsslag, og en delt «måling» ville
//     måttet være så vag at den ikke kunne si HVA sensoren målte. Det er stikk i
//     strid med hele poenget med den fila.
//
// `formaterAlder` og `formaterKlokke` bor i `friskhet.ts` og ikke her, selv om de
// er formaterere: de er en del av friskhetsmodellens ordforråd og brukes av
// `sensorStatus` selv, og en importpil fra friskhet til format ville vært en
// sirkel. Ett sted per regel er viktigere enn ryddige filnavn.
/** Norsk desimaltall med fast antall desimaler. */
function nb(v, desimaler) {
    return v.toFixed(desimaler).replace('.', ',');
}
/**
 * Peiling i grader, alltid tresifret (018°, ikke 18°) slik det gjøres muntlig
 * over samband. Desimalen vises BARE når usikkerheten er under en grad — ellers
 * påstår desimalen en presisjon peilingen ikke har.
 */
export function formaterPeiling(deg, usikkerhetDeg) {
    const n = ((deg % 360) + 360) % 360;
    const fin = usikkerhetDeg !== undefined && usikkerhetDeg < 1;
    const tall = fin ? nb(n, 1) : String(Math.round(n) % 360);
    const helTall = fin ? tall : tall.padStart(3, '0');
    return `${helTall}°`;
}
/** Usikkerhet som ±-tekst, eller tom streng når broen ikke oppgav den. */
export function formaterUsikkerhet(usikkerhetDeg) {
    if (usikkerhetDeg === undefined)
        return '';
    return usikkerhetDeg < 1 ? `±${nb(usikkerhetDeg, 1)}°` : `±${Math.round(usikkerhetDeg)}°`;
}
/**
 * Avstand. Under 1000 m i hele meter (der presisjonen betyr noe for
 * reaksjonstid), over i kilometer med én desimal. Ingen «1180 m» OG «1,2 km»
 * om hverandre i samme kolonne — det ødelegger sammenligning nedover lista.
 */
export function formaterAvstand(meter) {
    if (!Number.isFinite(meter))
        return '–';
    if (meter < 1000)
        return `${Math.round(meter)} m`;
    return `${nb(meter / 1000, 1)} km`;
}
/**
 * Frekvens. Skalaen velges av størrelsen, og desimalene av hva som skiller to
 * kanaler fra hverandre: kHz-oppløsning i MHz-båndet (VHF-kanaler ligger 25 kHz
 * fra hverandre) og MHz-oppløsning i GHz-båndet.
 */
export function formaterFrekvens(hz) {
    if (!Number.isFinite(hz) || hz <= 0)
        return '–';
    if (hz >= 1e9)
        return `${nb(hz / 1e9, 3)} GHz`;
    if (hz >= 1e6)
        return `${nb(hz / 1e6, 3)} MHz`;
    if (hz >= 1e3)
        return `${nb(hz / 1e3, 1)} kHz`;
    return `${Math.round(hz)} Hz`;
}
/** Båndbredde — samme skala-logikk, men grovere: bredden er en størrelsesorden. */
export function formaterBandbredde(hz) {
    if (!Number.isFinite(hz) || hz <= 0)
        return '–';
    if (hz >= 1e6)
        return `${nb(hz / 1e6, 1)} MHz`;
    if (hz >= 1e3)
        return `${Math.round(hz / 1e3)} kHz`;
    return `${Math.round(hz)} Hz`;
}
/**
 * Sikkerhet 0–1 → tekst. IKKE prosent.
 *
 * Prosent påstår kalibrering vi ikke har. Tre bånd med ord er ærligere og
 * raskere å lese: en operatør som ser «Høy» vet hva hun skal gjøre; en som ser
 * «73 %» begynner å regne på hva de 27 er.
 */
export function formaterSikkerhet(sikkerhet) {
    if (sikkerhet === undefined || !Number.isFinite(sikkerhet))
        return 'Ikke oppgitt';
    if (sikkerhet >= 0.8)
        return 'Høy';
    if (sikkerhet >= 0.5)
        return 'Middels';
    return 'Lav';
}
/** Variantnavn for CSS — samme tre bånd som `formaterSikkerhet`. */
export function sikkerhetsband(sikkerhet) {
    if (sikkerhet === undefined || !Number.isFinite(sikkerhet))
        return 'ukjent';
    if (sikkerhet >= 0.8)
        return 'høy';
    if (sikkerhet >= 0.5)
        return 'middels';
    return 'lav';
}
/** Kurs, samme tresifrede form som peiling. */
export function formaterKurs(kursDeg) {
    return kursDeg === undefined || !Number.isFinite(kursDeg) ? '–' : formaterPeiling(kursDeg);
}
//# sourceMappingURL=format.js.map